import React from 'react';
import { cn } from '../lib/utils';
import GlassCard from './GlassCard';

interface StatCardProps {
  title: string;
  value: string | number;
  change?: {
    value: number;
    percentage: boolean;
    period?: string;
  };
  icon?: React.ReactNode;
  variant?: 'default' | 'premium' | 'epic' | 'legendary';
  animated?: boolean;
  loading?: boolean;
  className?: string;
  trend?: 'up' | 'down' | 'neutral';
  description?: string;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  change,
  icon,
  variant = 'default',
  animated = true,
  loading = false,
  className,
  trend,
  description
}) => {
  const formatValue = (val: string | number) => {
    if (typeof val === 'number') {
      if (val >= 1000000) {
        return `${(val / 1000000).toFixed(1)}M`;
      } else if (val >= 1000) {
        return `${(val / 1000).toFixed(1)}K`;
      }
    }
    return val.toString();
  };

  const getTrendColor = () => {
    switch (trend) {
      case 'up':
        return 'text-green-400';
      case 'down':
        return 'text-red-400';
      default:
        return 'text-blue-400';
    }
  };

  const getTrendIcon = () => {
    switch (trend) {
      case 'up':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17l9.2-9.2M17 17V7H7" />
          </svg>
        );
      case 'down':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 7l-9.2 9.2M7 7v10h10" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        );
    }
  };

  if (loading) {
    return (
      <GlassCard variant={variant} animated={animated} className={className}>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="h-4 bg-white/20 rounded animate-pulse flex-1 mr-4" />
            <div className="w-8 h-8 bg-white/20 rounded animate-pulse" />
          </div>
          <div className="h-8 bg-white/20 rounded animate-pulse w-3/4" />
          <div className="h-3 bg-white/20 rounded animate-pulse w-1/2" />
        </div>
      </GlassCard>
    );
  }

  return (
    <GlassCard 
      variant={variant} 
      animated={animated} 
      className={cn('relative overflow-hidden', className)}
      glow={variant === 'legendary' || variant === 'epic'}
    >
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-gray-300 uppercase tracking-wider">
            {title}
          </h3>
          {icon && (
            <div className={cn(
              'p-2 rounded-lg',
              variant === 'legendary' && 'bg-gradient-to-br from-yellow-400/20 to-orange-500/20',
              variant === 'epic' && 'bg-gradient-to-br from-pink-500/20 to-purple-500/20',
              variant === 'premium' && 'bg-gradient-to-br from-purple-500/20 to-blue-500/20',
              variant === 'default' && 'bg-white/10'
            )}>
              <div className={cn(
                'w-6 h-6',
                variant === 'legendary' && 'text-yellow-400',
                variant === 'epic' && 'text-pink-400',
                variant === 'premium' && 'text-purple-400',
                variant === 'default' && 'text-white'
              )}>
                {icon}
              </div>
            </div>
          )}
        </div>

        {/* Main Value */}
        <div className="space-y-2">
          <div className={cn(
            'text-3xl font-bold tracking-tight',
            animated && 'transition-all duration-500',
            variant === 'legendary' && 'text-yellow-400 drop-shadow-lg',
            variant === 'epic' && 'text-pink-400 drop-shadow-lg',
            variant === 'premium' && 'text-purple-400 drop-shadow-lg',
            variant === 'default' && 'text-white'
          )}>
            {formatValue(value)}
          </div>

          {/* Change Indicator */}
          {change && (
            <div className={cn(
              'flex items-center space-x-1 text-sm',
              getTrendColor()
            )}>
              {getTrendIcon()}
              <span>
                {change.value > 0 ? '+' : ''}{change.value}
                {change.percentage && '%'}
              </span>
              {change.period && (
                <span className="text-gray-400">
                  {change.period}
                </span>
              )}
            </div>
          )}

          {/* Description */}
          {description && (
            <p className="text-xs text-gray-400 leading-relaxed">
              {description}
            </p>
          )}
        </div>
      </div>

      {/* Legendary particle effects */}
      {variant === 'legendary' && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-2 right-2 w-1 h-1 bg-yellow-400 rounded-full animate-ping opacity-60" />
          <div className="absolute bottom-4 left-4 w-0.5 h-0.5 bg-orange-400 rounded-full animate-pulse opacity-40" />
          <div className="absolute top-1/2 right-6 w-0.5 h-0.5 bg-red-400 rounded-full animate-bounce opacity-50" />
        </div>
      )}

      {/* Epic glow pulse */}
      {variant === 'epic' && (
        <div className="absolute inset-0 bg-gradient-to-br from-pink-500/5 to-purple-500/5 rounded-xl animate-pulse pointer-events-none" />
      )}
    </GlassCard>
  );
};

export default StatCard;